import React from 'react';
import uuidv4 from 'uuidv4';
import { Query } from 'react-apollo';
import { Storage } from 'aws-amplify';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Alert from '@material-ui/lab/Alert';
import Link from '@material-ui/core/Link';
import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Divider from '@material-ui/core/Divider';
import Form from 'components/form';
import Input from 'components/form/Input';
import Radio from 'components/form/Radio';
import Select from 'components/form/Select';
import PhoneInput from 'components/form/PhoneInput';
import DropZone from 'components/form/Dropzone';
import Button from 'components/common/Button';
import Spin from 'components/common/Spin';
import STATES from 'constant/states';
import extend from 'lodash/extend';
import { useSelector, useDispatch } from 'react-redux';
import { useMutation } from '@apollo/react-hooks';
import useSnack from 'hooks/useSnack';
import {
  UPDATE_STUDENT_PROFILE,
  GET_STUDENT_PROFILE,
} from 'graphql/student/Profile';
import { UPDATE_USER } from 'graphql/Home';
import { updateUser } from 'reducers/user';
import PERSONALITY_TYPE from 'constant/personality-types';
import GENDER from 'constant/gender-pronounce';
import TIMEZONE from 'constant/timezones';
import CITIZENSHIP from 'constant/citizenship';
import { validatePhone } from 'utils';
import useStyles from '../styles';
import CheckboxGroup from 'components/form/CheckboxGroup';
import InfoIcon from '@material-ui/icons/Info';

const BUCKET = process.env.REACT_APP_BUCKET;
const FormItem = Form.FormItem;

const Quiz = ({
  user,
  personal_info,
  disableCard,
  isSkip,
  saveText,
  onSkip,
  form: { getFieldDecorator, validateFields, setFieldsValue },
  onFinish,
}) => {
  const classes = useStyles();
  const [updateProfile] = useMutation(UPDATE_STUDENT_PROFILE);
  const snack = useSnack();

  const onUpdate = () => {
    return validateFields().then(async (values) => {
      values.personality_type = values.personality_type?.value;
      values = { ...values, completed: true };
      await updateProfile({
        variables: {
          id: user.id,
          input: {
            personal_info: extend(personal_info, values),
          },
        },
      });
      snack.success('Profile saved');
      onFinish && onFinish();
    });
  };

  const onRemoveFile = (file) => {
    Storage.remove(file.url || file.name);
    setFieldsValue({
      personality_result_url: null,
    });
  };

  const handleFileSelect = (files, onProgress, onSuccess, onError) => {
    const file = files[0];
    if (!file) {
      setFieldsValue({
        personality_result_url: null,
      });
    } else {
      const key = uuidv4() + '-' + file.name;

      Storage.put(key, file, {
        progressCallback: (progress) => {
          const pc = (progress.loaded / progress.total) * 100;
          if (onProgress) {
            onProgress({ percent: pc }, file);
          }
        },
      })
        .then((result) => {
          const url = `https://${BUCKET}.s3.us-east-2.amazonaws.com/public/${result.key}`;
          if (onSuccess) {
            onSuccess(url, file);
          }
          setFieldsValue({
            personality_result_url: url,
          });
        })
        .catch((err) => {
          if (onError) {
            onError(err, file);
          }
        });
    }
  };

  return (
    <Card className={classes.card} component={disableCard && 'span'}>
      <CardContent className={classes.cardContent}>
        <Typography gutterBottom variant="h5">
          Personality Quiz
        </Typography>
        <Box maxWidth={500} width="100%" marginTop={3}>
          <Box marginBottom={6}>
            <Alert
              icon={<InfoIcon fontSize="inherit" />}
              severity="info"
              className={classes.infoAlert}
            >
              <Typography variant="caption" className={classes.infoText}>
                Discover your personality type{' '}
                <Link
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://www.16personalities.com/"
                >
                  here.
                </Link>
              </Typography>
            </Alert>
          </Box>
          <Form>
            <FormItem>
              {getFieldDecorator('personality_type', {
                initialValue: PERSONALITY_TYPE.find(
                  (i) => i.value === personal_info?.personality_type,
                ),
                rules: [{ required: true, message: 'It is required' }],
              })(
                <Select
                  isMulti={false}
                  options={PERSONALITY_TYPE}
                  label="Personality Type Result"
                />,
              )}
            </FormItem>
            <FormItem>
              {getFieldDecorator('personality_result_url', {
                initialValue: personal_info?.personality_result_url,
              })(
                <DropZone
                  listType="card"
                  uploadType="screenshot"
                  attachments={
                    personal_info?.personality_result_url
                      ? [{ name: personal_info?.personality_result_url }]
                      : []
                  }
                  label="Upload a screenshot of your results here"
                  onUpload={handleFileSelect}
                  onRemove={onRemoveFile}
                />,
              )}
            </FormItem>
          </Form>
        </Box>
      </CardContent>
      {!disableCard && <Divider />}
      <CardContent className="actions">
        <Box display="flex" justifyContent="center">
          {isSkip && (
            <div style={{ marginRight: 24 }}>
              <Button
                size="large"
                onClick={onSkip}
                style={{ padding: '8px 48px' }}
                variant="outlined"
              >
                Skip
              </Button>
            </div>
          )}
          <Button
            size="large"
            variant="contained"
            color="primary"
            onClick={onUpdate}
          >
            {saveText || 'Save'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export const QuizForm = Form.create()(Quiz);

const GeneralDetails = ({
  user,
  isQuizForm,
  disableCard,
  isSkip,
  onSkip,
  saveText,
  step,
  form: { getFieldDecorator, validateFields, getFieldValue },
  history,
  editable,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const snack = useSnack();
  const currentUser = useSelector((s) => s.user.user);
  const [update] = useMutation(UPDATE_USER);
  const [updateProfile] = useMutation(UPDATE_STUDENT_PROFILE);

  const onSubmit = () => {
    return validateFields().then(async ({ immigration_details, ...values }) => {
      const timezoneValue = values.timezone?.value;
      values.state = values.state?.value;
      values.timezone = timezoneValue;

      try {
        await update({
          variables: {
            sub: user.sub,
            data: {
              ...values,
              gender_pronounce: values.gender_pronounce
                ? `{${values.gender_pronounce}}`
                : '',
            },
          },
        });
        await updateProfile({
          variables: {
            id: user.id,
            input: {
              immigration_details,
            },
          },
        });
        if (currentUser.id === user.id) {
          dispatch(
            updateUser({
              fname: values.user_first_name,
              lname: values.user_last_name,
              user_phone: values.user_phone,
              gender_pronounce: values.gender_pronounce,
              timezone: timezoneValue,
              state: values.state,
              user_preferred_name: values.user_preferred_name,
              completed: true,
            }),
          );
        }
        snack.success('Profile saved');
        step && history.push(`/onboarding/${step + 1}`);
      } catch (e) {
        const { message } = e;
        if (message.includes('users_user_phone_key')) {
          snack.error('Phone number already in use. Try a different number.');
        } else {
          snack.error(message);
        }
      }
    });
  };

  return (
    <Query
      query={GET_STUDENT_PROFILE}
      variables={{ id: user.id }}
      fetchPolicy={'cache-and-network'}
    >
      {({ data, loading }) => {
        if (!data) return <Spin spinning />;
        const profile = data.student_profile[0];
        const genders = Array.isArray(user?.gender_pronounce)
          ? user.gender_pronounce
          : user.gender_pronounce?.replace(/[{}]/g, '').split(', ');

        const immigration_details = getFieldValue('immigration_details');

        return (
          <Spin spinning={loading}>
            <Card className={classes.card} elevation={disableCard && 0}>
              <CardContent className={classes.cardContent}>
                <Typography variant="h5">Personal Info</Typography>
                <Box maxWidth={500} width="100%" marginTop={3}>
                  <Form>
                    <FormItem>
                      {getFieldDecorator('user_first_name', {
                        initialValue: user.fname,
                        rules: [
                          {
                            required: true,
                            message: 'First Name is required',
                          },
                        ],
                      })(
                        <Input
                          disabled={!editable}
                          labelStyle={{ fontSize: '14px' }}
                          inline
                          fullWidth
                          label="First name"
                        />,
                      )}
                    </FormItem>
                    <FormItem>
                      {getFieldDecorator('user_last_name', {
                        initialValue: user.lname,
                        rules: [
                          {
                            required: true,
                            message: 'Last Name is required',
                          },
                        ],
                      })(
                        <Input
                          disabled={!editable}
                          fullWidth
                          label="Last name"
                        />,
                      )}
                    </FormItem>
                    <FormItem>
                      {getFieldDecorator('user_preferred_name', {
                        initialValue: user.user_preferred_name,
                        rules: [{ required: false }],
                      })(
                        <Input
                          disabled={!editable}
                          fullWidth
                          label="Preferred name"
                        />,
                      )}
                    </FormItem>
                    {currentUser.type === 'admin' && (
                      <FormItem>
                        {getFieldDecorator('user_email', {
                          initialValue: user.user_email,
                          rules: [
                            { required: true, message: 'Email is required' },
                          ],
                        })(
                          <Input
                            disabled={!editable}
                            fullWidth
                            label="Email"
                          />,
                        )}
                      </FormItem>
                    )}
                    <FormItem>
                      {getFieldDecorator('user_phone', {
                        initialValue: user.user_phone,
                        rules: [
                          {
                            required: true,
                            message: 'Cell Phone is required',
                          },
                          { validator: validatePhone },
                        ],
                      })(
                        <PhoneInput
                          disabled={!editable}
                          fullWidth
                          label="Phone number"
                          placeholder="Enter cell phone number"
                        />,
                      )}
                    </FormItem>
                    <FormItem>
                      {getFieldDecorator('gender_pronounce', {
                        initialValue: genders,
                        rules: [{ required: true, message: 'It is required' }],
                      })(
                        <Radio
                          disabled={!editable}
                          row
                          options={GENDER}
                          setColor={disableCard && 'primary'}
                          label="Preferred Gender Pronouns"
                          size="small"
                        />,
                      )}
                    </FormItem>
                    <FormItem>
                      {getFieldDecorator('state', {
                        initialValue: STATES.find(
                          (s) => s.value === user.state,
                        ),
                        rules: [{ required: true, message: 'It is required' }],
                      })(
                        <Select
                          disabled={!editable}
                          isMulti={false}
                          options={STATES}
                          label="State of Residence"
                          description="Where will you get in-state tuition?"
                        />,
                      )}
                    </FormItem>
                    <FormItem>
                      {getFieldDecorator('timezone', {
                        initialValue: TIMEZONE.find(
                          (s) => s.value === user.timezone,
                        ),
                        rules: [{ required: true, message: 'It is required' }],
                      })(
                        <Select
                          disabled={!editable}
                          isMulti={false}
                          options={TIMEZONE}
                          label="Time zone"
                          description="Your primary time zone"
                        />,
                      )}
                    </FormItem>
                    <FormItem>
                      {getFieldDecorator('immigration_details.citizenship', {
                        initialValue: profile.immigration_details?.citizenship,
                        rules: [{ required: true, message: 'It is required' }],
                      })(
                        <Select
                          disabled={!editable}
                          transformValue
                          isMulti={false}
                          options={CITIZENSHIP}
                          label="Citizenship"
                          description="Select your citizenship status"
                        />,
                      )}
                    </FormItem>
                    {immigration_details?.citizenship === 'other' && (
                      <>
                        <FormItem>
                          {getFieldDecorator('immigration_details.has_visa', {
                            initialValue: profile.immigration_details?.has_visa,
                            rules: [
                              { required: true, message: 'It is required' },
                            ],
                          })(
                            <Radio
                              disabled={!editable}
                              row
                              label="Do you currently hold a valid U.S. visa?"
                              options={[
                                { label: 'Yes', value: 'yes' },
                                { label: 'No', value: 'no' },
                              ]}
                            />,
                          )}
                        </FormItem>
                        <FormItem>
                          {getFieldDecorator('immigration_details.details', {
                            initialValue: profile.immigration_details?.details,
                          })(
                            <Input
                              disabled={!editable}
                              labelStyle={{ fontSize: '14px' }}
                              fullWidth
                              multiline
                              rows={3}
                              label="Please indicate that visa type or explain your situation further if you intend to apply for a new or different visa."
                            />,
                          )}
                        </FormItem>
                      </>
                    )}
                  </Form>
                </Box>
              </CardContent>
              {!disableCard && <Divider />}
              <CardContent className={classes.footer}>
                <Box display="flex" justifyContent="center">
                  {isSkip && (
                    <div style={{ marginRight: 24 }}>
                      <Button size="large" variant="outlined" onClick={onSkip}>
                        Skip
                      </Button>
                    </div>
                  )}
                  <Button
                    size="large"
                    variant="contained"
                    color="primary"
                    onClick={onSubmit}
                    disabled={!editable}
                  >
                    {saveText || 'Save'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
            {isQuizForm && (
              <QuizForm user={user} personal_info={profile.personal_info} />
            )}
          </Spin>
        );
      }}
    </Query>
  );
};

export default Form.create()(GeneralDetails);
